import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Copy, Inbox, LoaderCircle, X } from "lucide-react";
import { api } from "../lib/api";

// Leads live under Pixel & websites, next to People and Events: the list of
// Meta instant-form submissions the pixel account has received. Where they are
// sent (Zapier or any https endpoint) is a setting under Settings > Integrations.

type Lead = {
  id: string; whop_event_id: string; meta_lead_id: string | null; source: string; event_time: string;
  campaign_id: string | null; campaign_name: string | null; ad_group_name: string | null; ad_name: string | null;
  answers: Record<string, string>; questions: Array<{ key?: string; question?: string; answer?: string | null }>;
  contact_email: string | null; contact_phone: string | null; contact_name: string | null;
  delivery_status: "pending" | "delivered" | "retrying" | "failed" | "skipped"; delivered_at: string | null;
};
type WebhookConfig = { configured: boolean; url: string; enabled: boolean; secret: string | null; last_status: number | null; last_error: string | null; last_delivery_at: string | null; updated_at: string | null };
type Delivery = { id: string; lead_id: string | null; kind: "lead" | "test"; attempt: number; status: "pending" | "success" | "failed" | "dead"; response_code: number | null; error: string | null; next_attempt_at: string | null; attempted_at: string | null; created_at: string };
type LeadsResponse = { leads: Lead[]; webhook: WebhookConfig; deliveries: Delivery[]; synced_through: string | null; can_manage: boolean; counts: { total_30d: number; delivered: number; retrying: number; failed: number } };
type Result = { ok: boolean; status: number | null; error: string | null };
type Notice = { tone: "ok" | "bad"; text: string };

function when(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return "";
  const date = new Date(parsed);
  const now = new Date();
  const time = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (date.toDateString() === now.toDateString()) return `Today ${time}`;
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
  return `${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${time}`;
}
function hostOf(url: string): string { try { return new URL(url).hostname.replace(/^hooks\./, ""); } catch { return url; } }
function hasName(lead: Lead): boolean { return Boolean(lead.contact_name && lead.contact_name !== lead.contact_email); }

// ---------------------------------------------------------------- the list (Pixel & websites > Leads)

export function LeadsList({ onOpenSettings }: { onOpenSettings: () => void }) {
  const [data, setData] = useState<LeadsResponse | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setData(await api<LeadsResponse>("/api/ads/leads")); setError(""); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Leads could not be loaded."); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!selected) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setSelected(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);
  useEffect(() => {
    if (!notice) return;
    const handle = window.setTimeout(() => setNotice(null), 6000);
    return () => window.clearTimeout(handle);
  }, [notice]);

  const run = async (key: string, action: () => Promise<Notice | null>) => {
    setBusy(key); setError("");
    try { const result = await action(); if (result) setNotice(result); await load(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Something went wrong."); }
    finally { setBusy(""); }
  };
  const retry = (lead: Lead) => run(`retry:${lead.id}`, async () => {
    const result = await api<Result>(`/api/ads/leads/${encodeURIComponent(lead.id)}/retry`, { method: "POST" });
    return result.ok ? { tone: "ok", text: `Sent to ${hostOf(data?.webhook.url || "")}.` } : { tone: "bad", text: `Not accepted: ${result.error || "no response"}. It will retry on its own.` };
  });
  const checkNow = () => run("sync", async () => {
    const result = await api<{ fetched: number; inserted: number }>("/api/ads/leads/sync", { method: "POST" });
    return { tone: "ok", text: result.inserted > 0 ? `${result.inserted} new lead${result.inserted === 1 ? "" : "s"}.` : "Nothing new since the last check." };
  });
  const attemptsByLead = useMemo(() => {
    const map = new Map<string, Delivery[]>();
    for (const delivery of data?.deliveries || []) if (delivery.lead_id) map.set(delivery.lead_id, [...(map.get(delivery.lead_id) || []), delivery]);
    return map;
  }, [data]);

  if (error && !data) return <div className="surface-state surface-state--error"><strong>Leads could not be loaded</strong><span>{error}</span><button className="button button--secondary button--small" type="button" onClick={() => void load()}>Retry</button></div>;
  if (!data) return <div className="leads-loading"><LoaderCircle className="spin" /> Loading leads…</div>;

  const webhook = data.webhook;
  const connected = webhook.configured && webhook.enabled;
  const current = selected ? data.leads.find((lead) => lead.id === selected) || null : null;

  return <div className="leads">
    <header className="leads-head">
      <div>
        <strong>{data.leads.length === 0 ? "No leads yet" : `${data.counts.total_30d} lead${data.counts.total_30d === 1 ? "" : "s"} in the last 30 days`}</strong>
        <span>
          Checked {data.synced_through ? when(data.synced_through) : "not yet"} · <button type="button" className="leads-link" disabled={busy === "sync"} onClick={() => void checkNow()}>{busy === "sync" ? "Checking…" : "Check now"}</button>
        </span>
      </div>
      {data.can_manage && <div className="leads-head__dest">
        {notice && <span className={`leads-toast is-${notice.tone}`} role="status">{notice.text}</span>}
        {connected
          ? <span className="leads-dest is-ok"><i />Sent to {hostOf(webhook.url)} · <button type="button" className="leads-link" onClick={onOpenSettings}>Settings</button></span>
          : webhook.configured
            ? <span className="leads-dest is-paused"><i />Sending paused · <button type="button" className="leads-link" onClick={onOpenSettings}>Resume</button></span>
            : <span className="leads-dest"><i />Not sent anywhere yet · <button type="button" className="leads-link" onClick={onOpenSettings}>Connect Zapier or a CRM</button></span>}
      </div>}
    </header>
    {error && <div className="form-alert" role="alert">{error}</div>}

    {data.leads.length === 0 && <div className="leads-empty"><Inbox /><strong>Nothing here yet</strong><span>When someone submits a Meta instant form on one of your ads, they appear here within about 20 minutes.</span></div>}

    {data.leads.length > 0 && <div className="leads-scroll"><table className="leads-table">
      <thead><tr><th>Lead</th><th>Phone</th><th>Campaign</th><th>Received</th>{webhook.configured && <th className="is-right">Sent</th>}</tr></thead>
      <tbody>
        {data.leads.map((lead) => <tr key={lead.id} className={selected === lead.id ? "is-selected" : ""} onClick={() => setSelected(lead.id)}>
          <td>
            <strong>{hasName(lead) ? lead.contact_name : lead.contact_email || "Lead"}</strong>
            {hasName(lead) && lead.contact_email && <small>{lead.contact_email}</small>}
          </td>
          <td className="leads-mono">{lead.contact_phone || <span className="leads-dim">—</span>}</td>
          <td>{lead.campaign_name ? <><span>{lead.campaign_name}</span>{lead.ad_name && <small>{lead.ad_name}</small>}</> : <span className="leads-dim">—</span>}</td>
          <td className="leads-nowrap">{when(lead.event_time)}</td>
          {webhook.configured && <td className="is-right">{lead.delivery_status === "delivered" ? <span className="leads-sent is-ok"><Check /> Sent</span> : lead.delivery_status === "pending" || lead.delivery_status === "retrying" ? <span className="leads-sent is-wait">Sending…</span> : <span className="leads-sent is-no">Not sent</span>}</td>}
        </tr>)}
      </tbody>
    </table></div>}

    {current && <div className="leads-drawer-backdrop" role="presentation" onClick={() => setSelected(null)}>
      <aside className="leads-drawer" role="dialog" aria-modal="true" aria-label={`Lead ${hasName(current) ? current.contact_name : current.contact_email || ""}`} onClick={(event) => event.stopPropagation()}>
        <header>
          <div><strong>{hasName(current) ? current.contact_name : current.contact_email || "Lead"}</strong><span>Received {when(current.event_time)}</span></div>
          <button type="button" className="leads-iconbtn" aria-label="Close" onClick={() => setSelected(null)}><X /></button>
        </header>
        <div className="leads-drawer__body">
          <section>
            <h4>Contact</h4>
            <dl>
              <div><dt>Email</dt><dd>{current.contact_email ? <a href={`mailto:${current.contact_email}`}>{current.contact_email}</a> : "—"}</dd></div>
              <div><dt>Phone</dt><dd>{current.contact_phone ? <a href={`tel:${current.contact_phone}`}>{current.contact_phone}</a> : "—"}</dd></div>
            </dl>
          </section>
          <section>
            <h4>Answers</h4>
            {current.questions.length === 0 && Object.keys(current.answers).length === 0 && <p className="leads-dim">No form answers were recorded for this lead.</p>}
            <dl>
              {current.questions.map((question, index) => <div key={index}><dt>{question.question || question.key || "Question"}</dt><dd>{question.answer ?? <span className="leads-dim">not answered</span>}</dd></div>)}
              {current.questions.length === 0 && Object.entries(current.answers).map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}
            </dl>
          </section>
          <section>
            <h4>From</h4>
            <dl>
              <div><dt>Campaign</dt><dd>{current.campaign_name || "—"}</dd></div>
              <div><dt>Ad group</dt><dd>{current.ad_group_name || "—"}</dd></div>
              <div><dt>Ad</dt><dd>{current.ad_name || "—"}</dd></div>
              <div><dt>Meta lead ID</dt><dd className="leads-mono">{current.meta_lead_id || "—"}</dd></div>
            </dl>
          </section>
          {webhook.configured && <section>
            <h4>Delivery</h4>
            {(() => {
              const attempts = attemptsByLead.get(current.id) || [];
              const last = attempts[0];
              return <>
                <p>
                  {current.delivery_status === "delivered" && `Sent to ${hostOf(webhook.url)} ${when(current.delivered_at || last?.attempted_at)}, accepted${last?.response_code ? ` with HTTP ${last.response_code}` : ""}.`}
                  {(current.delivery_status === "pending" || current.delivery_status === "retrying") && `Queued for ${hostOf(webhook.url)}${last?.next_attempt_at ? `, next try ${when(last.next_attempt_at)}` : ""}.`}
                  {current.delivery_status === "failed" && `Could not reach ${hostOf(webhook.url)} after ${attempts.length || "several"} tries${last?.error ? `: ${last.error}` : ""}.`}
                  {current.delivery_status === "skipped" && "Received before a destination was connected, so it was never sent."}
                </p>
                {attempts.length > 1 && <ul className="leads-attempts">{attempts.map((attempt) => <li key={attempt.id}><span>{when(attempt.attempted_at || attempt.created_at)}</span><span>{attempt.status === "success" ? `Accepted (HTTP ${attempt.response_code})` : attempt.status === "pending" ? "Queued" : attempt.error || "Failed"}</span></li>)}</ul>}
                {data.can_manage && webhook.enabled && current.delivery_status !== "pending" && <button type="button" className="leads-btn" disabled={busy === `retry:${current.id}`} onClick={() => void retry(current)}>{busy === `retry:${current.id}` ? "Sending…" : current.delivery_status === "delivered" ? "Send again" : "Send now"}</button>}
              </>;
            })()}
          </section>}
        </div>
      </aside>
    </div>}
  </div>;
}

// ---------------------------------------------------------------- the setting (Settings > Integrations)

export function LeadDeliverySettings() {
  const [data, setData] = useState<LeadsResponse | null>(null);
  const [error, setError] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);
  const [testResult, setTestResult] = useState<Result | null>(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try { const payload = await api<LeadsResponse>("/api/ads/leads"); setData(payload); setUrl((value) => value || payload.webhook.url); setError(""); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "The lead settings could not be loaded."); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const act = async (key: string, action: () => Promise<string>) => {
    setBusy(key); setError(""); setNotice("");
    try { setNotice(await action()); await load(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save."); }
    finally { setBusy(""); }
  };
  const save = (enabled: boolean) => act("save", async () => {
    await api("/api/ads/leads/webhook", { method: "POST", body: JSON.stringify({ url: url.trim(), enabled }) });
    return data?.webhook.configured ? "Destination updated." : "Connected. Send a test event so Zapier can learn the fields.";
  });
  const toggle = () => act("toggle", async () => {
    if (!data) return "";
    await api("/api/ads/leads/webhook", { method: "POST", body: JSON.stringify({ url: data.webhook.url, enabled: !data.webhook.enabled }) });
    return data.webhook.enabled ? "Paused. Leads are still collected, just not sent." : "Sending resumed.";
  });
  const test = async () => {
    setBusy("test"); setError(""); setTestResult(null); setNotice("");
    try { setTestResult(await api<Result>("/api/ads/leads/webhook/test", { method: "POST" })); await load(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "The test could not be sent."); }
    finally { setBusy(""); }
  };
  const copySecret = () => { if (!data?.webhook.secret) return; void navigator.clipboard.writeText(data.webhook.secret).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1600); }); };

  if (error && !data) return <section className="settings-card"><div className="form-alert" role="alert" style={{ margin: 24 }}>{error}</div></section>;
  if (!data) return <section className="settings-card"><div className="settings-row"><div><strong>Loading…</strong></div></div></section>;
  const webhook = data.webhook;
  const connected = webhook.configured;
  const dirty = url.trim() !== webhook.url;

  return <section className="settings-card leads-settings">
    <div className="settings-card__head">
      <span className={`leads-settings__dot ${!connected ? "" : webhook.enabled ? (webhook.last_error ? "is-bad" : "is-ok") : "is-paused"}`} />
      <div>
        <strong>Leads</strong>
        <small>
          {!connected && "Not connected. New leads are collected under Pixel & websites but not sent anywhere."}
          {connected && webhook.enabled && !webhook.last_error && `Every new lead is sent to ${hostOf(webhook.url)}${webhook.last_delivery_at ? `. Last sent ${when(webhook.last_delivery_at)}.` : "."}`}
          {connected && webhook.enabled && webhook.last_error && `${hostOf(webhook.url)} is not accepting leads: ${webhook.last_error}${webhook.last_status ? ` (HTTP ${webhook.last_status})` : ""}. Failed leads retry for 12 hours.`}
          {connected && !webhook.enabled && `Sending to ${hostOf(webhook.url)} is paused.`}
        </small>
      </div>
    </div>
    {error && <div className="form-alert" role="alert" style={{ margin: "0 24px 16px" }}>{error}</div>}
    {notice && <p className="accounts-notice" role="status">{notice}</p>}
    {testResult && <p className={`accounts-notice ${testResult.ok ? "" : "is-error"}`}>{testResult.ok ? `Test event received (HTTP ${testResult.status}). In Zapier, click Test trigger and pick it as the sample.` : `Your endpoint did not accept the test: ${testResult.error || "no response"}.`}</p>}

    <div className="settings-row leads-settings__row">
      <div>
        <strong>Where leads go</strong>
        <span>A Zapier Catch Hook URL, or any https endpoint that accepts JSON.</span>
      </div>
      <form className="leads-settings__form" onSubmit={(event) => { event.preventDefault(); void save(true); }}>
        <input value={url} inputMode="url" placeholder="https://hooks.zapier.com/hooks/catch/…" aria-label="Webhook URL" onChange={(event) => { setUrl(event.target.value); setError(""); }} />
        {(!connected || dirty) && <button type="submit" className="button button--primary button--small" disabled={busy === "save" || url.trim().length < 12}>{busy === "save" ? "Saving…" : connected ? "Save" : "Connect"}</button>}
      </form>
    </div>

    {connected && <div className="settings-row">
      <div>
        <strong>Sending</strong>
        <span>{webhook.enabled ? "On. Each new lead is sent once, with automatic retries if your endpoint is down." : "Paused. Leads are still collected here."}</span>
      </div>
      <div className="leads-settings__actions">
        <button type="button" className="button button--secondary button--small" disabled={busy === "test" || !webhook.enabled || dirty} onClick={() => void test()}>{busy === "test" ? "Sending…" : "Send test event"}</button>
        <button type="button" className="button button--secondary button--small" disabled={busy === "toggle"} onClick={() => void toggle()}>{webhook.enabled ? "Pause" : "Resume"}</button>
      </div>
    </div>}

    {!connected && <div className="settings-row leads-settings__steps">
      <ol>
        <li><strong>In Zapier,</strong> create a Zap. Trigger: Webhooks by Zapier, Catch Hook. Copy the URL it shows.</li>
        <li><strong>Paste it above</strong> and click Connect, then Send test event.</li>
        <li><strong>Back in Zapier,</strong> click Test trigger, pick the sample, and map <code>contact</code> and <code>answers</code> into your CRM step.</li>
      </ol>
    </div>}

    {connected && <details className="settings-row leads-settings__dev">
      <summary>Developer details</summary>
      <p>One POST per lead with body <code>{"{ event, event_id, created_at, data }"}</code>. <code>event_id</code> is identical on retries: use it to dedupe. Signature header <code>X-OrangeTrail-Signature: t=&lt;unix time&gt;,v1=&lt;HMAC SHA-256 of "t.body"&gt;</code> with this secret:</p>
      <span className="leads-dev__secret"><code>{webhook.secret}</code><button type="button" aria-label="Copy signing secret" onClick={copySecret}>{copied ? <Check /> : <Copy />}</button></span>
      <p>Retries after 1 min, 5 min, 30 min, 2 h and 12 h, then manual from the lead. Respond 2xx; redirects count as failures.</p>
    </details>}
  </section>;
}
